import React, { useState } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { FullLogo, Icon } from '../components';
import { colors, spacing, borderRadius, fontWeights } from '../theme';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M02LoginScreen'>;

export const M02LoginScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = () => {
    // Navigate directly to HomeScreen
    navigation.replace('M03HomeScreen');
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <View style={styles.header}>
        <FullLogo width={220} />
      </View>

      <View style={styles.form}>
        <View style={styles.inputContainer}>
          <Text style={styles.label}>Email hoặc SĐT</Text>
          <View style={styles.inputWrapper}>
            <Icon name="mail" size={20} color={colors.onSurfaceVariant} style={styles.inputIcon} />
            <TextInput
              style={styles.input}
              placeholder="nhap.email@example.com"
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>Mật khẩu</Text>
          <View style={styles.inputWrapper}>
            <Icon name="lock" size={20} color={colors.onSurfaceVariant} style={styles.inputIcon} />
            <TextInput
              style={styles.input}
              placeholder="••••••"
              value={password}
              onChangeText={setPassword}
              secureTextEntry={!showPassword}
              autoCapitalize="none"
            />
            <TouchableOpacity onPress={() => setShowPassword(!showPassword)} style={styles.eyeIcon}>
              <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={20} color={colors.onSurfaceVariant} />
            </TouchableOpacity>
          </View>
        </View>

        <TouchableOpacity style={styles.forgotBtn}>
          <Text style={styles.forgotText}>Quên mật khẩu?</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.loginBtn} onPress={handleLogin}>
          <Text style={styles.loginBtnText}>ĐĂNG NHẬP</Text>
        </TouchableOpacity>

        <View style={styles.dividerContainer}>
          <View style={styles.dividerLine} />
          <Text style={styles.dividerText}>HOẶC</Text>
          <View style={styles.dividerLine} />
        </View>

        <TouchableOpacity style={styles.googleBtn} onPress={handleLogin}>
          <Icon name="face" size={20} color={colors.primary} style={styles.googleIcon} />
          <Text style={styles.googleBtnText}>Đăng nhập bằng Google</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>
          Chưa có tài khoản? <Text style={styles.registerText}>Đăng ký ngay</Text>
        </Text>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: '#ffffff',
    padding: spacing[6],
    justifyContent: 'center',
  },
  header: {
    alignItems: 'center',
    marginBottom: spacing[8],
  },
  title: {
    fontFamily: "'Be Vietnam Pro', sans-serif",
    fontSize: 28,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    marginTop: spacing[2],
  },
  subtitle: {
    fontSize: 14,
    color: colors.onSurfaceVariant,
    marginTop: spacing[1],
  },
  form: {
    width: '100%',
  },
  inputContainer: {
    marginBottom: spacing[4],
  },
  label: {
    fontSize: 14,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
    marginBottom: spacing[2],
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    paddingHorizontal: spacing[3],
    backgroundColor: colors.surface,
    height: 48,
  },
  inputIcon: {
    marginRight: spacing[2],
  },
  input: {
    flex: 1,
    height: '100%',
    color: colors.onSurface,
    fontSize: 15,
  },
  eyeIcon: {
    padding: spacing[1],
  },
  forgotBtn: {
    alignSelf: 'flex-end',
    marginBottom: spacing[6],
  },
  forgotText: {
    color: colors.primary,
    fontSize: 13,
    fontWeight: fontWeights.semibold,
  },
  loginBtn: {
    backgroundColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  loginBtnText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: fontWeights.bold,
    letterSpacing: 0.5,
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing[5],
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.outlineVariant,
  },
  dividerText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    paddingHorizontal: spacing[3],
  },
  googleBtn: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
  },
  googleIcon: {
    marginRight: spacing[2],
  },
  googleBtnText: {
    color: colors.primary,
    fontSize: 14,
    fontWeight: fontWeights.bold,
  },
  footer: {
    alignItems: 'center',
    marginTop: spacing[8],
  },
  footerText: {
    fontSize: 14,
    color: colors.onSurfaceVariant,
  },
  registerText: {
    color: colors.primary,
    fontWeight: fontWeights.bold,
  },
});
